import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, HomeIcon, SearchIcon, ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
interface PartnerBranding {
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  backUrl: string;
  programTitle?: string;
}
export function ThankYouPage() {
  usePageMeta('Thank You | OnlineCollegeGuide', 'Your information request has been submitted. A school representative will be in touch shortly.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as any)?.formData;
  const partner = (location.state as any)?.partner as PartnerBranding | undefined;
  if (partner) {
    return <main className="min-h-screen flex flex-col" style={{
      backgroundColor: '#F8FAFB'
    }} data-id="element-947">
        {/* Partner Header */}
        <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm border-b border-gray-100" data-id="element-948">
          {partner.logo ? <img src={partner.logo} alt={`${partner.name} Logo`} className="h-14 object-contain" data-id="element-949" /> : <span className="text-xl font-bold" style={{
          color: partner.primaryColor
        }} data-id="element-950">
              {partner.name}
            </span>}
        </header>

        {/* Hero */}
        <div className="relative overflow-hidden py-16 md:py-24" style={{
        background: `linear-gradient(135deg, ${partner.primaryColor} 0%, ${partner.primaryColor}dd 60%, ${partner.accentColor}88 100%)`
      }} data-id="element-951">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-952">
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} className="mx-auto mb-6" data-id="element-953">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto border-2" style={{
              backgroundColor: `${partner.accentColor}20`,
              borderColor: `${partner.accentColor}40`
            }} data-id="element-954">
                <CheckCircleIcon className="w-10 h-10" style={{
                color: partner.accentColor
              }} data-id="element-955" />
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-956">
              Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="text-lg text-white/90 mb-2" data-id="element-957">
              Your information has been submitted to{' '}
              <strong className="text-white" data-id="element-958">{partner.name}</strong>.
            </motion.p>

            {partner.programTitle && <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-base text-white/70" data-id="element-959">
                Program: {partner.programTitle}
              </motion.p>}

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="text-base text-white/70 max-w-md mx-auto mt-3" data-id="element-960">
              A representative from {partner.name} will reach out to you shortly
              to discuss your education options.
            </motion.p>
          </div>
        </div>

        {/* Steps Card */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16 w-full" data-id="element-961">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.55
        }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-962">
            <h2 className="text-lg font-bold mb-4" style={{
            color: partner.primaryColor
          }} data-id="element-963">
              What Happens Next?
            </h2>
            <div className="space-y-4" data-id="element-964">
              <div className="flex items-start gap-4" data-id="element-965">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-966">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-967">
                    1
                  </span>
                </div>
                <div data-id="element-968">
                  <p className="font-semibold text-gray-800" data-id="element-969">
                    Application Review
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-970">
                    {partner.name} will review your information and match you
                    with the best program options.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-971">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-972">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-973">
                    2
                  </span>
                </div>
                <div data-id="element-974">
                  <p className="font-semibold text-gray-800" data-id="element-975">
                    Expect a Call or Email
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-976">
                    An admissions advisor from {partner.name} will contact you
                    {formData?.email ? ` at ${formData.email}` : ''} to discuss
                    next steps.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-977">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-978">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-979">
                    3
                  </span>
                </div>
                <div data-id="element-980">
                  <p className="font-semibold text-gray-800" data-id="element-981">
                    Enroll & Get Started
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-982">
                    Once you're ready, your advisor at {partner.name} will guide
                    you through the enrollment process.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-983">
              <button onClick={() => navigate(partner.backUrl)} className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-md transition-colors text-sm" style={{
              backgroundColor: partner.primaryColor
            }} onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'} data-id="element-984">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-985" />
                Back to {partner.name}
              </button>
              <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 border font-semibold rounded-md transition-colors text-sm" style={{
              borderColor: partner.primaryColor,
              color: partner.primaryColor
            }} data-id="element-986">
                <HomeIcon className="w-4 h-4" data-id="element-987" />
                Explore More Schools
              </button>
            </div>
          </motion.div>
        </div>
      </main>;
  }
  // Default generic thank you page
  return <main className="flex-grow bg-light" data-id="element-988">
      <div className="relative overflow-hidden bg-gradient-to-r from-[#1B365D] via-[#102442] to-[#8B1A1A] py-16 md:py-24" data-id="element-989">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-990">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} className="mx-auto mb-6" data-id="element-991">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto border border-white/20" data-id="element-992">
              <CheckCircleIcon className="w-10 h-10 text-gold" data-id="element-993" />
            </div>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-994">
            Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35
        }} className="text-lg text-gray-300 mb-2" data-id="element-995">
            Your information has been submitted successfully.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45
        }} className="text-base text-gray-400 max-w-md mx-auto" data-id="element-996">
            A representative from one of our partner schools will reach out to
            you shortly to discuss your education options.
          </motion.p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16" data-id="element-997">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.55
      }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-998">
          <h2 className="text-lg font-bold text-navy mb-4" data-id="element-999">
            What Happens Next?
          </h2>
          <div className="space-y-4" data-id="element-1000">
            <div className="flex items-start gap-4" data-id="element-1001">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1002">
                <span className="text-sm font-bold text-navy" data-id="element-1003">1</span>
              </div>
              <div data-id="element-1004">
                <p className="font-semibold text-gray-800" data-id="element-1005">
                  Review Your Matches
                </p>
                <p className="text-sm text-gray-600" data-id="element-1006">
                  Our system is matching you with accredited programs that fit
                  your goals and preferences.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-1007">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1008">
                <span className="text-sm font-bold text-navy" data-id="element-1009">2</span>
              </div>
              <div data-id="element-1010">
                <p className="font-semibold text-gray-800" data-id="element-1011">
                  Expect a Call or Email
                </p>
                <p className="text-sm text-gray-600" data-id="element-1012">
                  An admissions advisor from a matched school will contact you
                  {formData?.email ? ` at ${formData.email}` : ''} to discuss
                  next steps.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-1013">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1014">
                <span className="text-sm font-bold text-navy" data-id="element-1015">3</span>
              </div>
              <div data-id="element-1016">
                <p className="font-semibold text-gray-800" data-id="element-1017">
                  Enroll & Get Started
                </p>
                <p className="text-sm text-gray-600" data-id="element-1018">
                  Once you've found the right fit, your advisor will guide you
                  through the enrollment process.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-1019">
            <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 bg-navy hover:bg-navy-light text-white font-semibold rounded-md transition-colors text-sm" data-id="element-1020">
              <HomeIcon className="w-4 h-4" data-id="element-1021" />
              Back to Home
            </button>
            <button onClick={() => navigate('/colleges')} className="flex items-center justify-center gap-2 px-6 py-3 border border-navy text-navy hover:bg-navy/5 font-semibold rounded-md transition-colors text-sm" data-id="element-1022">
              <SearchIcon className="w-4 h-4" data-id="element-1023" />
              Browse More Schools
            </button>
          </div>
        </motion.div>
      </div>
    </main>;
}